import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useSearchParams } from 'src/utils/useSearchParams'
import { useNavigate } from 'src/utils/useNavigate';
import { postInvitationFromOrganizationAPI } from 'src/serverApi/invitations';
import { getAuthorizationStateAPI, getLogoutAPI } from 'src/serverApi/authorization';
import { postAcceptInvitationToOrganizationAPI } from 'src/serverApi/organizationUsers';
import routes from 'src/constants/routes';
import PageSpin from 'src/components/PageSpin';
import ContentLayout from 'src/components/ContentLayout';
import SBPLogo from 'src/components/SBPLogo';
import Button from 'src/components/Button';
import Text from 'src/components/Text';
import HelpLink from 'src/components/HelpLink';
import styles from './styles.module.scss';

const AcceptInvitation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const confirmationId = searchParams.get('confirmation');

  const [isLoading, setIsLoading] = useState(false);
  const [invitationData, setInvitationData] = useState<any>();
  const [userId, setUserId] = useState<any>();
  
  if (!confirmationId) {
    navigate(routes.main);
  }

  const init = async (confirmationId: any) => {
    try {
      setIsLoading(true);
      
      const [data, authorizationState] = await Promise.all([
        postInvitationFromOrganizationAPI(confirmationId),
        getAuthorizationStateAPI(),
      ]);
      
      setInvitationData(data);
      setUserId(authorizationState?.userId)
    } catch (error: any) {
      if (error.type === 'NOT_FOUND') {
        toast.error('Ссылка недействительна. Получите новую ссылку и перейдите по ней.');
        return;
      }
      toast.error('При загрузке данных приглашения произошла ошибка');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    init(confirmationId);
  }, [confirmationId]);
  
  const acceptInvitation = async () => {
    try {
      await postAcceptInvitationToOrganizationAPI(invitationData?.organizationUeid, confirmationId || '');
      navigate(routes.agent);
    } catch (error: any) {
      if (error.type === 'NOT_FOUND') {
        toast.error('Ссылка недействительна. Получите новую ссылку и перейдите по ней.');
        return;
      }
      toast.error('Произошла ошибка. Попробуйте позже.');
    }
  }
  
  const changeUser = async () => {
    try {
      await getLogoutAPI();
      window.location.reload();
    } catch (error) {
      toast.error('Произошла ошибка. Попробуйте позже.');
    }
  }
  
  const handleAccept = () => {
    acceptInvitation();
  }
  
  const handleChangeUser = () => {
    changeUser();
  }
  
  const handleAuthorizationClick = () => {
    navigate({
      pathname: routes.authorization,
      search: searchParams.toString(),
    });
  };
  
  const handleRegistrationClick = () => {
    navigate({
      pathname: routes.registration,
      search: searchParams.toString(),
    });
  };

  if (isLoading) {
    return <PageSpin />;
  }
  
  if (!invitationData) {
    return null;
  }
  
  if (userId) {
    return (
      <ContentLayout size="small">
        <SBPLogo className={styles['sbp-logo']} />
        <Text type="h2" className={styles['title']}>Приглашение</Text>
        <Text type="p" muted>Вы вошли под пользователем: <Text type="span">{userId}</Text></Text>
        <Text type="p" muted>
          Можете принять приглашение в организацию <Text type="span">{invitationData.organizationName} ИНН{' '}
          {invitationData.organizationInn} ОГРН {invitationData.organizationOgrn}{' '}</Text>
          для этого пользователя или войти под другим
        </Text>
        <div className={styles['authorised-buttons']}>
          <Button onClick={handleAccept} className={styles['button']}>Принять приглашение</Button>
          <Button
            variant="outlined"
            onClick={handleChangeUser}
            className={styles['button']}
          >
            Войти под другим пользователем
          </Button>
        </div>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout size="small">
      <HelpLink className={styles['help']}/>
      <SBPLogo className={styles['sbp-logo']} />
      <Text type="h2" className={styles['title']}>Приглашение</Text>
      <Text type="p" muted>
        После входа вы примите приглашение в организацию <Text type="span">{invitationData.organizationName} ИНН{' '}
        {invitationData.organizationInn} ОГРН {invitationData.organizationOgrn}</Text>
      </Text>
      <div className={styles['not-authorised-buttons']}>
        <Button onClick={handleAuthorizationClick} className={styles['button']}>Войти</Button>
        <Button
          variant="outlined"
          onClick={handleRegistrationClick}
          className={styles['button']}
        >
          Зарегистрироваться
        </Button>
      </div>
    </ContentLayout>
  );
};

export default AcceptInvitation;
